/**
 * Global per-user chart preferences: favorite timeframes, favorite drawing
 * tools, the tool wheel's layout, and the news sidebar chart's timeframe.
 *
 * A tiny module-level store shared by every chart, so favoriting a timeframe
 * (or a drawing tool) on one chart updates all of them. Backed by the
 * `chartPreferences` global chart-asset; loaded once on first use, persisted
 * optimistically on each toggle (reverts on failure). Exposed via
 * `useChartPreferences()` over `useSyncExternalStore` — no provider placement
 * to worry about.
 *
 * Every save sends EVERY field: the asset is one record and the API PUTs it
 * whole, so a save that carried only one list would wipe the others.
 */
import { useSyncExternalStore, useEffect } from 'react'
import { OVERLAY_GROUPS, PERIODS, sanitizeWheelSlots, type PeriodKey } from '@edge/charts'
import { loadChartPreferences, saveChartPreferences } from './chart-assets-api'

const VALID_PERIOD_KEYS = new Set(Object.keys(PERIODS))
const VALID_TOOL_NAMES = new Set(OVERLAY_GROUPS.flatMap((g) => g.tools.map((t) => t.name)))

/**
 * The drawing rail's shortlist before the user has starred anything (Chris,
 * 2026-09-12). Seeded only when the stored record has NO drawing field; an
 * explicit `[]` is respected, since re-seeding would fight a user who cleared
 * it. Four of these carry Option chords (see toolHotkeys).
 */
export const DEFAULT_FAVORITE_DRAWING_TOOLS: readonly string[] = [
  'rayLine',
  'horizontalStraightLine',
  'fibonacciLine',
  'measure',
  'positionPlan',
]

/** The news sidebar chart's timeframe until the user picks one. */
export const DEFAULT_NEWS_CHART_PERIOD: PeriodKey = '5m'

/** A real PeriodKey, else the news chart default. */
function sanitizeNewsChartPeriod(key: unknown): PeriodKey {
  return typeof key === 'string' && VALID_PERIOD_KEYS.has(key)
    ? (key as PeriodKey)
    : DEFAULT_NEWS_CHART_PERIOD
}

/** Keep only catalog overlay names, de-duplicated, preserving first-seen order. */
function sanitizeTools(names: readonly string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const n of names) {
    if (VALID_TOOL_NAMES.has(n) && !seen.has(n)) {
      seen.add(n)
      out.push(n)
    }
  }
  return out
}

/** Keep only real PeriodKeys, de-duplicated, preserving first-seen order. */
function sanitize(keys: string[]): PeriodKey[] {
  const seen = new Set<string>()
  const out: PeriodKey[] = []
  for (const k of keys) {
    if (VALID_PERIOD_KEYS.has(k) && !seen.has(k)) {
      seen.add(k)
      out.push(k as PeriodKey)
    }
  }
  return out
}

let favorites: PeriodKey[] = []
let drawingFavorites: string[] = sanitizeTools(DEFAULT_FAVORITE_DRAWING_TOOLS)
let wheelSlots: string[] = sanitizeWheelSlots(undefined)
let newsChartPeriod: PeriodKey = DEFAULT_NEWS_CHART_PERIOD
let loadState: 'idle' | 'loading' | 'loaded' = 'idle'
/** The record actually arrived (a 404 counts: first use). False after a failed load. */
let loadSucceeded = false
const listeners = new Set<() => void>()

function emit(): void {
  for (const l of listeners) l()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): PeriodKey[] {
  return favorites
}

function getDrawingSnapshot(): string[] {
  return drawingFavorites
}

function getWheelSnapshot(): string[] {
  return wheelSlots
}

function getNewsChartPeriodSnapshot(): PeriodKey {
  return newsChartPeriod
}

/** The whole record, as the API wants it. */
function persist(): Promise<void> {
  return saveChartPreferences({
    favoriteTimeframes: favorites,
    favoriteDrawingTools: drawingFavorites,
    toolWheelSlots: wheelSlots,
    newsChartPeriod,
  })
}

/** Load the persisted favorites once. Safe to call from every chart mount. */
export async function ensureChartPreferencesLoaded(): Promise<void> {
  if (loadState !== 'idle') return
  loadState = 'loading'
  try {
    const prefs = await loadChartPreferences()
    favorites = sanitize(prefs.favoriteTimeframes)
    // Absent field = never set, seed. Present (even empty) = the user's list.
    drawingFavorites = sanitizeTools(prefs.favoriteDrawingTools ?? DEFAULT_FAVORITE_DRAWING_TOOLS)
    // Absent = the default wheel; a saved layout is repaired, never dropped.
    wheelSlots = sanitizeWheelSlots(prefs.toolWheelSlots)
    newsChartPeriod = sanitizeNewsChartPeriod(prefs.newsChartPeriod)
    loadSucceeded = true
  } catch {
    // Network/auth hiccup → stay empty / default; a later toggle still persists.
    favorites = []
    drawingFavorites = sanitizeTools(DEFAULT_FAVORITE_DRAWING_TOOLS)
    wheelSlots = sanitizeWheelSlots(undefined)
  } finally {
    loadState = 'loaded'
    emit()
  }
}

/** Toggle a timeframe in/out of favorites, persisting optimistically. */
export function toggleFavoriteTimeframe(key: PeriodKey): void {
  if (!VALID_PERIOD_KEYS.has(key)) return
  const prev = favorites
  favorites = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
  emit()
  void persist().catch(() => {
    // Revert so the UI never claims a save that didn't land.
    favorites = prev
    emit()
  })
}

/** Toggle a drawing tool in/out of the rail shortlist, persisting optimistically. */
export function toggleFavoriteDrawingTool(name: string): void {
  if (!VALID_TOOL_NAMES.has(name)) return
  const prev = drawingFavorites
  drawingFavorites = prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
  emit()
  void persist().catch(() => {
    drawingFavorites = prev
    emit()
  })
}

/**
 * Replace the tool wheel's layout, persisting optimistically. Ignored until
 * the record has loaded: the save PUTs every field, so saving before then
 * would write default favorites over the user's real ones.
 */
export function setToolWheelSlots(next: readonly string[]): void {
  if (!loadSucceeded) return
  const clean = sanitizeWheelSlots(next)
  if (clean.length === wheelSlots.length && clean.every((name, i) => name === wheelSlots[i])) return
  const prev = wheelSlots
  wheelSlots = clean
  emit()
  void persist().catch(() => {
    wheelSlots = prev
    emit()
  })
}

/**
 * Set the news sidebar chart's timeframe. The chart switches at once. A pick
 * before the record loads is kept for this session but not saved (a save then
 * would PUT default favorites over the real ones), and a load that lands
 * afterwards replaces it. Rare: every chart panel loads the record on mount.
 * A failed save keeps the pick (no revert): snapping the chart back to the
 * old timeframe would be worse than the choice lasting this session only.
 */
export function setNewsChartPeriod(key: PeriodKey): void {
  if (!VALID_PERIOD_KEYS.has(key) || key === newsChartPeriod) return
  newsChartPeriod = key
  emit()
  if (!loadSucceeded) return
  void persist().catch(() => {})
}

/** Test-only: reset module state between tests. */
export function __resetChartPreferences(): void {
  favorites = []
  drawingFavorites = sanitizeTools(DEFAULT_FAVORITE_DRAWING_TOOLS)
  wheelSlots = sanitizeWheelSlots(undefined)
  newsChartPeriod = DEFAULT_NEWS_CHART_PERIOD
  loadState = 'idle'
  loadSucceeded = false
  listeners.clear()
}

export function useChartPreferences(): {
  favoriteTimeframes: PeriodKey[]
  toggleFavoriteTimeframe: (key: PeriodKey) => void
  favoriteDrawingTools: string[]
  toggleFavoriteDrawingTool: (name: string) => void
  toolWheelSlots: string[]
  setToolWheelSlots: (slots: readonly string[]) => void
  newsChartPeriod: PeriodKey
  setNewsChartPeriod: (key: PeriodKey) => void
} {
  const favoriteTimeframes = useSyncExternalStore(subscribe, getSnapshot)
  const favoriteDrawingTools = useSyncExternalStore(subscribe, getDrawingSnapshot)
  const toolWheelSlots = useSyncExternalStore(subscribe, getWheelSnapshot)
  const newsChartPeriod = useSyncExternalStore(subscribe, getNewsChartPeriodSnapshot)
  useEffect(() => {
    void ensureChartPreferencesLoaded()
  }, [])
  return {
    favoriteTimeframes,
    toggleFavoriteTimeframe,
    favoriteDrawingTools,
    toggleFavoriteDrawingTool,
    toolWheelSlots,
    setToolWheelSlots,
    newsChartPeriod,
    setNewsChartPeriod,
  }
}
